import PromisePool from '@supercharge/promise-pool';
import { AxiosInstance } from 'axios';

const DEFAULT_PAGE_SIZE = 200;
const DEFAULT_CONCURRENCY = 2;
const MAX_PAGES = 100;

export interface PaginatedFetchOptions {
  pageSize?: number;
  concurrency?: number;
  maxPages?: number;
  signal?: AbortSignal;
}

export async function fetchAllPaginated<T>(
  axiosInstance: AxiosInstance,
  endpoint: string,
  params: Record<string, any> = {},
  options: PaginatedFetchOptions = {},
): Promise<T[]> {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    concurrency = DEFAULT_CONCURRENCY,
    maxPages = MAX_PAGES,
    signal,
  } = options;

  if (params.page || params.pageSize) {
    const response = await axiosInstance.get<T[]>(endpoint, { params, signal });
    return response.data;
  }

  const firstResponse = await axiosInstance.get<T[]>(endpoint, {
    params: { ...params, page: 1, pageSize },
    signal,
  });

  const firstPageData = firstResponse.data;

  if (!Array.isArray(firstPageData) || firstPageData.length < pageSize) {
    return firstPageData;
  }

  const allData: T[] = [...firstPageData];
  let currentPage = 2;
  let hasMore = true;

  while (hasMore && currentPage <= maxPages) {
    const batchSize = Math.min(concurrency, maxPages - currentPage + 1);
    const pages = Array.from(
      { length: batchSize },
      (_, i) => currentPage + i,
    );

    const { results, errors } = await PromisePool
      .for(pages)
      .withConcurrency(concurrency)
      .process(async (page) => {
        const response = await axiosInstance.get<T[]>(endpoint, {
          params: { ...params, page, pageSize },
          signal,
        });
        return { page, data: response.data };
      });

    if (errors.length > 0) {
      throw errors[0];
    }

    results.sort((a, b) => a.page - b.page);

    for (const result of results) {
      allData.push(...result.data);
      if (!Array.isArray(result.data) || result.data.length < pageSize) {
        hasMore = false;
        break;
      }
    }

    currentPage += batchSize;
  }

  return allData;
}
